import { useEffect, useRef } from "react";
import { X, Minus, Plus, ShoppingBag, ShoppingCart, Trash2, Package } from "lucide-react";
import { useCart } from "./CartContext";
import { useIsCatalogMode } from "./useStoreMode";
import { useSiteNavigate } from "./SiteLink";
import { formatPrice } from "../../storeSiteConfig";
import { useCartCheckoutSettings } from "../../utils/cartCheckoutSettings";

/**
 * CartDrawer — Slide-in panel from the left showing cart contents.
 * Includes quantity controls, item removal, total, and CTA buttons
 * that navigate to the full Cart page and Checkout page.
 *
 * Now consumes cartCheckoutSettings for:
 *  - freeShippingThreshold (instead of hardcoded promoStrip value)
 *  - emptyCartTitle / emptyCartDescription
 *  - ctaColor
 */
export function CartDrawer() {
  const {
    items,
    totalItems,
    totalPrice,
    removeItem,
    updateQuantity,
    clearCart,
    isCartOpen,
    closeCart,
  } = useCart();

  const isCatalog = useIsCatalogMode();
  const drawerRef = useRef<HTMLDivElement>(null);
  const navigate = useSiteNavigate();
  const { settings } = useCartCheckoutSettings();

  // Close on Escape
  useEffect(() => {
    if (!isCartOpen || isCatalog) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCart();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isCartOpen, closeCart, isCatalog]);

  // Lock body scroll when open
  useEffect(() => {
    if (isCatalog) return;
    if (isCartOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isCartOpen, isCatalog]);

  // In catalog mode, the cart drawer is not needed
  if (isCatalog) return null;

  if (!isCartOpen) return null;

  const threshold = settings.freeShippingThreshold;
  const freeShippingDelta = threshold - totalPrice;
  const hasFreeShipping = totalPrice >= threshold;

  const goToCart = () => {
    closeCart();
    navigate("/cart");
  };

  const goToCheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  return (
    <div className="fixed inset-0 z-[9998]" dir="rtl">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={closeCart}
      />

      {/* Drawer panel — slides from left (LTR sense = right in RTL) */}
      <div
        ref={drawerRef}
        className="absolute top-0 left-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col"
        style={{ animation: "slideInLeft 0.3s ease-out" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-zap to-zap-dark rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-zap-navy text-base leading-tight" style={{ fontWeight: 700 }}>
                {"\u05E1\u05DC \u05E7\u05E0\u05D9\u05D5\u05EA"}
              </h2>
              <p className="text-xs text-gray-400 leading-tight mt-0.5">
                {totalItems === 0
                  ? "\u05D4\u05E1\u05DC \u05E8\u05D9\u05E7"
                  : totalItems === 1
                    ? "\u05E4\u05E8\u05D9\u05D8 \u05D0\u05D7\u05D3"
                    : `${totalItems} \u05E4\u05E8\u05D9\u05D8\u05D9\u05DD`}
              </p>
            </div>
          </div>
          <span
            role="button"
            tabIndex={0}
            onClick={closeCart}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") closeCart(); }}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
          >
            <X className="w-5 h-5" />
          </span>
        </div>

        {/* Free shipping progress */}
        {totalItems > 0 && threshold > 0 && (
          <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-100">
            {hasFreeShipping ? (
              <p className="text-emerald-700 text-xs text-center" style={{ fontWeight: 600 }}>
                {"\u05D6\u05DB\u05D0\u05D9\u05DD \u05DC\u05DE\u05E9\u05DC\u05D5\u05D7 \u05D7\u05D9\u05E0\u05DD!"}
              </p>
            ) : (
              <div>
                <p className="text-emerald-700 text-xs text-center mb-1.5" style={{ fontWeight: 500 }}>
                  {`\u05D7\u05E1\u05E8\u05D9\u05DD ${formatPrice(freeShippingDelta)} \u05DC\u05DE\u05E9\u05DC\u05D5\u05D7 \u05D7\u05D9\u05E0\u05DD`}
                </p>
                <div className="w-full bg-emerald-200 rounded-full h-1.5">
                  <div
                    className="bg-emerald-500 h-1.5 rounded-full"
                    style={{ width: `${Math.min((totalPrice / threshold) * 100, 100)}%`, transition: "width 0.3s" }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Package className="w-16 h-16 text-gray-200 mb-4" />
              <p className="text-gray-400 text-sm mb-1" style={{ fontWeight: 600 }}>
                {settings.emptyCartTitle}
              </p>
              <p className="text-gray-300 text-xs">
                {settings.emptyCartDescription}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => {
                const variationDesc = Object.values(item.selectedVariations).filter(Boolean).join(" / ");
                const uniqueKey = `${item.productSlug}-${Object.entries(item.selectedVariations).map(([k, v]) => `${k}${v}`).join("")}`;
                return (
                  <div
                    key={uniqueKey}
                    className="flex gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100"
                  >
                    {/* Image placeholder */}
                    <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center shrink-0">
                      <Package className="w-6 h-6 text-gray-400" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-zap-navy text-sm leading-tight truncate" style={{ fontWeight: 600 }}>
                        {item.name}
                      </h4>
                      {variationDesc && (
                        <p className="text-gray-400 text-[11px] mt-0.5 truncate">
                          {variationDesc}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        {/* Quantity controls */}
                        <div className="flex items-center gap-1.5">
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={() => updateQuantity(item.productSlug, item.selectedVariations, item.quantity - 1)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") updateQuantity(item.productSlug, item.selectedVariations, item.quantity - 1); }}
                            className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-100"
                          >
                            <Minus className="w-3 h-3 text-gray-500" />
                          </span>
                          <span className="text-sm text-zap-navy w-6 text-center" style={{ fontWeight: 600 }}>
                            {item.quantity}
                          </span>
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={() => updateQuantity(item.productSlug, item.selectedVariations, item.quantity + 1)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") updateQuantity(item.productSlug, item.selectedVariations, item.quantity + 1); }}
                            className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-100"
                          >
                            <Plus className="w-3 h-3 text-gray-500" />
                          </span>
                        </div>

                        {/* Price */}
                        <span className="text-zap-navy text-sm" style={{ fontWeight: 700 }}>
                          {formatPrice(item.price * item.quantity)}
                        </span>
                      </div>
                    </div>

                    {/* Remove */}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => removeItem(item.productSlug, item.selectedVariations)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") removeItem(item.productSlug, item.selectedVariations); }}
                      className="p-1 text-gray-300 hover:text-red-500 cursor-pointer self-start"
                    >
                      <Trash2 className="w-4 h-4" />
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-gray-100 px-5 py-4 space-y-3 bg-white">
            {/* Summary */}
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-sm">{"\u05E1\u05D4\"\u05DB"}</span>
              <span className="text-zap-navy text-xl" style={{ fontWeight: 800 }}>
                {formatPrice(totalPrice)}
              </span>
            </div>

            {/* View full cart button */}
            <span
              role="button"
              tabIndex={0}
              onClick={goToCart}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") goToCart(); }}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm cursor-pointer hover:bg-gray-200"
              style={{ fontWeight: 600 }}
            >
              <ShoppingCart className="w-4 h-4" />
              {"\u05E6\u05E4\u05D9\u05D9\u05D4 \u05D1\u05E1\u05DC \u05D4\u05DE\u05DC\u05D0"}
            </span>

            {/* Checkout button — uses ctaColor from settings */}
            <span
              role="button"
              tabIndex={0}
              onClick={goToCheckout}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") goToCheckout(); }}
              className="w-full flex items-center justify-center gap-2 px-5 py-3.5 text-white rounded-xl text-sm cursor-pointer shadow-lg"
              style={{ fontWeight: 700, backgroundColor: settings.ctaColor }}
            >
              <ShoppingBag className="w-4 h-4" />
              {"\u05DC\u05E7\u05D5\u05E4\u05D4"}
            </span>

            {/* Clear cart */}
            <span
              role="button"
              tabIndex={0}
              onClick={clearCart}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") clearCart(); }}
              className="block text-center text-xs text-gray-400 hover:text-red-500 cursor-pointer py-1"
            >
              {"\u05E8\u05D9\u05E7\u05D5\u05DF \u05D4\u05E1\u05DC"}
            </span>
          </div>
        )}
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
